import { debug, log, warn } from 'cc';
import type { MediaClient } from './media-client';
import StreamingPlayerAdapter, { CommonStreamingErrorName } from '../StreamingPlayerAdapter';
import StreamingError from '../../StreamingError';

declare module './media-client' {
    // eslint-disable-next-line @typescript-eslint/naming-convention, @typescript-eslint/no-shadow, @typescript-eslint/no-unused-vars
    interface MediaClient<T extends {}> {
        connected: boolean;
        loadingRoomId: string;
        loadRoomAndConnectPromise: Promise<void>;
        loadRoomAndConnect(roomId: string): Promise<void>;
    }
}

export enum YanHauStreamingErrorName {
    ROUTER_NOT_FOUND = 'ROUTER_NOT_FOUND',
    DUPLCATE_ROUTER = 'DUPLCATE_ROUTER',
    TRANSPORT_NOT_FOUND = 'TRANSPORT_NOT_FOUND',
    TRANSPORT_NOT_MATCH = 'TRANSPORT_NOT_MATCH',
    CONSUMER_NOT_FOUND = 'CONSUMER_NOT_FOUND',
    PRODUCER_NOT_FOUND = 'PRODUCER_NOT_FOUND',
    PRODUCER_UNAVAILABLE = 'PRODUCER_UNAVAILABLE',
    PROFILE_UNAVAILABLE = 'PROFILE_UNAVAILABLE',
    INVAILD_JWT = 'INVAILD_JWT',
    UNAUTHORIZED = 'UNAUTHORIZED',
    WEBSOCKET_AUTH_FAIL = 'WEBSOCKET_AUTH_FAIL',
}

type YanHauStreamingError = {
    errorType: 'AppDefineError';
    code: number;
    message: string;
};

enum MediaClientEventType {
    RoomOpen = 'room:open',
    RoomEnter = 'room:enter',
    RoomNew = 'room:new',
    RoomClose = 'room:close',
    ProducerNew = 'produce:new',
    ProducerClose = 'produce:close',
    StreamPause = 'stream:pause',
    StreamResume = 'stream:resume',
    Open = 'open',
    Error = 'error',
}

function extendsMediaClient() {
    async function loadRoomAndConnectPromise(mediaClient: MediaClient<null>, roomId: string) {
        mediaClient.loadingRoomId = roomId;
        mediaClient.connected = false;
        await mediaClient.loadRoom(roomId);
        await mediaClient.connect();
        mediaClient.connected = true;
        mediaClient.loadingRoomId = '';
    }
    window.MediaClient.prototype.loadRoomAndConnect = function tryLoadRoomAndConnect(this: MediaClient<null>, roomId: string) {
        if (this.currentRoom === roomId && this.connected) {
            return Promise.resolve();
        }
        if (this.loadingRoomId !== roomId) {
            this.loadRoomAndConnectPromise = loadRoomAndConnectPromise(this, roomId);
        }
        return this.loadRoomAndConnectPromise;
    };
}

let mediaClient: MediaClient<MediaClientEventType>;

export default class YanHauStreamingPlayerAdapter extends StreamingPlayerAdapter {
    private _videoPlayPromise: Promise<void> = null;
    private _currentRoomId: string = '';
    private _currentProducerIds: string[] = [];

    constructor(public readonly videoElement: HTMLVideoElement) {
        super();
        if (!mediaClient) {
            mediaClient = new window.MediaClient();
            // @ts-ignore
            mediaClient.signal.forwardEvent(true);
            mediaClient.on(MediaClientEventType.RoomClose, (routerId) => {
                if (this._currentRoomId === routerId) {
                    log(`Room(${routerId}) is closed`);
                    this.emit(YanHauStreamingPlayerAdapter.EVENT_TYPE.Error, new StreamingError(CommonStreamingErrorName.OTHER_ERROR, 'room closed'));
                }
            });
            mediaClient.on(MediaClientEventType.ProducerClose, (producerId) => {
                if (this._currentProducerIds.includes(producerId)) {
                    log(`Producer(${producerId}) is closed`);
                    this.emit(YanHauStreamingPlayerAdapter.EVENT_TYPE.Error, new StreamingError(CommonStreamingErrorName.OTHER_ERROR, 'producer closed'));
                }
            });
            mediaClient.on(MediaClientEventType.Error, (error: YanHauStreamingError) => {
                if (error?.errorType === 'AppDefineError') {
                    const matchedResult = error.message.match(/^ErrorCode: (.+)$/);
                    if (matchedResult) {
                        const errorName = matchedResult[1];
                        this.emit(YanHauStreamingPlayerAdapter.EVENT_TYPE.Error, new StreamingError(errorName as YanHauStreamingErrorName, error));
                    } else {
                        this.emit(YanHauStreamingPlayerAdapter.EVENT_TYPE.Error, new StreamingError(CommonStreamingErrorName.UNKNOWN_ERROR, error));
                        warn(`Unmatched app define error: ${error.message}`);
                    }
                } else {
                    this.emit(YanHauStreamingPlayerAdapter.EVENT_TYPE.Error, new StreamingError(CommonStreamingErrorName.OTHER_ERROR, error));
                }
            });
            Object.values(MediaClientEventType).forEach((eventType) => {
                mediaClient.on(eventType, (...event) => {
                    debug(`MediaClient event: ${eventType}`, ...event);
                });
            });
            extendsMediaClient();
        }
        if (videoElement.requestVideoFrameCallback === undefined) {
            warn('HTMLVideoElement does not support requestVideoFrameCallback, listening to play event instead');
            videoElement.addEventListener('play', () => {
                this.emit(YanHauStreamingPlayerAdapter.EVENT_TYPE.Play);
            });
        }
        videoElement.addEventListener('error', (event) => {
            this.emit(YanHauStreamingPlayerAdapter.EVENT_TYPE.Error, new StreamingError(CommonStreamingErrorName.OTHER_ERROR, event.message));
        });
        this.videoElement.addEventListener('resize', this.onResizeCallback);
    }

    public get muted(): boolean {
        return this.videoElement.muted;
    }
    public set muted(value: boolean) {
        this.videoElement.muted = value;
    }

    public get volume(): number {
        return this.videoElement.volume;
    }
    public set volume(value: number) {
        this.videoElement.volume = value;
    }

    public get controls(): boolean {
        return this.videoElement.controls;
    }
    public set controls(value: boolean) {
        this.videoElement.controls = value;
    }

    public get playsinline(): boolean {
        return this.videoElement.playsInline;
    }
    public set playsinline(value: boolean) {
        this.videoElement.playsInline = value;
    }

    public get loop(): boolean {
        return this.videoElement.loop;
    }
    public set loop(value: boolean) {
        this.videoElement.loop = value;
    }

    public get height(): number {
        return this.videoElement.videoHeight;
    }

    public get width(): number {
        return this.videoElement.videoWidth;
    }

    public async play(roomID: string, producerID: string | string[]): Promise<void> {
        await mediaClient.loadRoomAndConnect(roomID);
        if (this.videoElement.srcObject) {
            // remove all existing tracks
            const mediaStream = this.videoElement.srcObject as MediaStream;
            mediaStream.getTracks().forEach((track) => {
                track.stop();
                mediaStream.removeTrack(track);
            });
        } else {
            this.videoElement.srcObject = new MediaStream();
        }
        if (!producerID || (Array.isArray(producerID) && producerID.length === 0)) {
            warn('No producerID provided');
            return;
        }
        try {
            const mediaStream = this.videoElement.srcObject as MediaStream;
            const producerIDs = Array.isArray(producerID) ? producerID : [producerID];
            await Promise.all(producerIDs.map((id) => mediaClient.consume(id).then((consumer) => mediaStream.addTrack(consumer.track))));
            this._videoPlayPromise = this.videoElement.play();
            this.videoElement.requestVideoFrameCallback?.((...args) => {
                log('Video frame update:', ...args);
                this.emit(YanHauStreamingPlayerAdapter.EVENT_TYPE.Play);
            });
            await this._videoPlayPromise;
        } catch (error) {
            this.emit(YanHauStreamingPlayerAdapter.EVENT_TYPE.Error, error);
        }
    }

    public stop(): void {
        if (this.videoElement.paused) {
            return;
        }
        this.pause();
        const mediaStream = this.videoElement.srcObject as MediaStream;
        if (!mediaStream) {
            return;
        }
        mediaStream.getTracks().forEach((track) => {
            track.stop();
            mediaStream.removeTrack(track);
        });
        // clear the video element source
        this.videoElement.srcObject = null;
        this.load();
        mediaClient.connected = false;
    }

    public resume() {
        this._videoPlayPromise = this.videoElement.play();
        return this._videoPlayPromise;
    }

    public pause(): void {
        (this._videoPlayPromise ?? Promise.resolve()).then(() => {
            this.videoElement.pause();
            this._videoPlayPromise = null;
        });
    }

    public load(): void {
        (this._videoPlayPromise ?? Promise.resolve()).then(() => {
            this.videoElement.load();
            this._videoPlayPromise = null;
        });
    }
}
